import React, {FC} from 'react';

import {getHasNewSynchronousP3} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {useConversationStatusActionOfflinePermission} from '../../../../../../srcCommon/permissions/useOfflinePermission';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {DefaultSendActionsEnum} from '../../../../../models/api/preferencesModel';
import {SendMessage, SendToolbarButton} from './sendToolbarButton';

/*
 * Props.
 */

export interface SendToolbarButtonContainerProps {
  cellsContext: CellsContext;
  conversation: ConversationModel | null;
  hasValidationErrors?: boolean;
  areSendShortcutsDisabled?: boolean;
  hasClosePermission: boolean;
  hasSendPermission: boolean;
  defaultSendAction: DefaultSendActionsEnum;
  sendMessage: SendMessage;
  hasTrialExpired?: boolean;
  hasTeamTrialExpired?: boolean;
  timelineVariation: TimelineVariationsEnum;
  hasArchivePermission: boolean;
}

/*
 * Component.
 */

export const SendToolbarButtonContainer: FC<SendToolbarButtonContainerProps> = (props) => {
  // Check whether we're allowed to archive/snooze the conversation when we send.
  const updateConversationStatusOfflinePermission = useConversationStatusActionOfflinePermission(
    props.cellsContext,
    props.conversation ? [props.conversation] : [],
  );
  const hasNewSynchronousP3 = useAppSelector(getHasNewSynchronousP3);

  const hasSynchronousTimeline = props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS;

  function computeAllowedDefaultSendAction(): DefaultSendActionsEnum {
    if (hasNewSynchronousP3) {
      return updateConversationStatusOfflinePermission.isAllowed && props.hasArchivePermission
        ? props.defaultSendAction
        : DefaultSendActionsEnum.SEND;
    }

    return updateConversationStatusOfflinePermission.isAllowed &&
      props.hasArchivePermission &&
      !hasSynchronousTimeline
      ? props.defaultSendAction
      : DefaultSendActionsEnum.SEND;
  }

  // If we're not allowed to archive/snooze, make the send button the default.
  const allowedDefaultSendAction = computeAllowedDefaultSendAction();

  // Check if trial has expired to disable sending
  const canUserSend = props.hasSendPermission && !props.hasTrialExpired && !props.hasTeamTrialExpired;

  return (
    <SendToolbarButton
      hasValidationErrors={props.hasValidationErrors}
      areSendShortcutsDisabled={props.areSendShortcutsDisabled}
      hasClosePermission={props.hasClosePermission}
      hasSendPermission={canUserSend}
      updateConversationStatusOfflinePermission={updateConversationStatusOfflinePermission}
      defaultSendAction={allowedDefaultSendAction}
      sendMessage={props.sendMessage}
      conversation={props.conversation}
      hasSynchronousTimeline={hasSynchronousTimeline}
    />
  );
};
